import { apiBase } from './apiBase';
import { ApiError } from './api';
import type { ApprovalStatus, SocialPlatform, SocialPostFormat } from './socialPostsApi';

// The client's shared content calendar (backend/src/routes/clientCalendarPublic.ts).
//
// Plain fetch on purpose, not apiFetch: the bearer here is the PIN session,
// not a Firmio login, and apiFetch would swap it for a colleague's login token
// on a 401 (session refresh) — or serve Test Mode data.

export interface PublicPost {
  id: string;
  title: string;
  platform: SocialPlatform;
  format: SocialPostFormat;
  publishAt: string;
  caption: string | null;
  mediaUrl: string | null;
  approvalStatus: ApprovalStatus;
}

async function call<T>(path: string, init: RequestInit = {}, session?: string): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (session) headers.set('Authorization', `Bearer ${session}`);
  const res = await fetch(`${apiBase()}/api/client-calendar/public/${path}`, { ...init, headers });
  const body = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { message?: string; code?: string };
  } | null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error?.message ?? 'Ceva n-a mers.', body?.error?.code);
  }
  return body?.data as T;
}

const enc = encodeURIComponent;

export function getLobby(token: string) {
  return call<{ clientName: string }>(enc(token));
}

export function unlock(token: string, pin: string) {
  return call<{ session: string }>(`${enc(token)}/unlock`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

export function listPublicPosts(token: string, session: string, from: string, to: string) {
  const qs = new URLSearchParams({ from, to }).toString();
  return call<{ posts: PublicPost[] }>(`${enc(token)}/posts?${qs}`, {}, session);
}

export function decide(
  token: string,
  session: string,
  postId: string,
  action: 'approve' | 'request-changes',
  input: { name: string; comment?: string },
) {
  return call<PublicPost>(
    `${enc(token)}/posts/${enc(postId)}/${action}`,
    { method: 'POST', body: JSON.stringify(input) },
    session,
  );
}
